import { createContext, useContext, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import type { PropsWithChildren, ReactElement } from 'react'
import type { IAppManagement } from '~/types/application-management'
import type { DeviceType, MobileDeviceType } from '~/types/device-type'

import { COOKIES, DEFAULT_APPLICATION } from '~/const'
import { useAppsManagementDetailsQuery } from '~/queries/application-management'
import { displayToast } from '~/utils/toast'

interface IAppSummaryContext {
  appDetails?: IAppManagement
  appKey: string
  deviceType: DeviceType
  isError: boolean
  isLoading: boolean
}

const AppSummaryContext = createContext<IAppSummaryContext>({
  appKey: '',
  deviceType: 'ios',
  isError: false,
  isLoading: false,
})

const AppSummaryProvider = ({ children }: PropsWithChildren): ReactElement => {
  /** Local state */

  const [searchParams] = useSearchParams()

  const appParam = searchParams.get('app')

  const appLocalStorage = localStorage.getItem(COOKIES.STORED_APPLICATION)

  const app = appParam ? appParam : appLocalStorage || DEFAULT_APPLICATION

  const [appKey, deviceType] = app.split(':')

  const { data: appDetails, isError, isLoading } = useAppsManagementDetailsQuery(appKey)

  /** Effects */

  useEffect(() => {
    if (appParam && appParam !== appLocalStorage) {
      localStorage.setItem(COOKIES.STORED_APPLICATION, appParam)
    }
  }, [appLocalStorage, appParam])

  if (isError) displayToast('Application specified in URL could not be found', 'error')

  return (
    <AppSummaryContext.Provider
      value={{
        appDetails,
        appKey,
        deviceType: deviceType as MobileDeviceType,
        isError: isError,
        isLoading: isLoading,
      }}
    >
      {children}
    </AppSummaryContext.Provider>
  )
}

export const useAppSummary = (): IAppSummaryContext => {
  return useContext(AppSummaryContext)
}

export default AppSummaryProvider
